<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, onMounted, onBeforeMount } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import { useUiStore } from "@/stores/ui";
import { useAgentInstructionsStore } from "@/stores/agentInstructions";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import moment from "moment";
import {
  DxDataGrid,
  DxColumn,
  // DxFilterRow,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
  DxColumnChooser,
  DxLoadPanel,
  DxStateStoring,
  DxSearchPanel,
} from "devextreme-vue/data-grid";
// import DxDropDownButton from "devextreme-vue/drop-down-button";
// import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import ActionCell from "../components/agentInstructions/ActionCell.vue";
import deleteAgentInstructions from "../components/agentInstructions/deleteAgentInstructions.vue";
import router from "@/router";
import { useCompanyStore } from "@/stores/company";

const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId")
);

const uiStore = useUiStore();
const agentInstructionsStore = useAgentInstructionsStore();
const gridPreferencesStore = useGridPreferencesStore();
let getModalState = computed(() => uiStore.modalState);
const dataGridRefName = ref(null);
let showClearAll = ref(false);
const allowedOperations = ref(["contains"]);
// const buttonData = ref(["Excel", "CSV", "PDF"]);
let promptsGrid = ref([]);
// const state = reactive({
//   promptModal: null,
// });

const dataGrid = computed(() => {
  return dataGridRefName.value.instance;
});

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  let allColumns = Array.from(Array(dataGrid.value.columnCount()).keys()).map(
    (index) => dataGrid.value.columnOption(index)
  );
  allColumns = allColumns.find((col) => col.sortIndex != null);
  return allColumns;
};

const setColumnChooserPosition = () => {
  var columnChooserView = dataGrid.value.getView("columnChooserView");
  if (!columnChooserView._popupContainer) {
    columnChooserView._initializePopupContainer();
    columnChooserView.render();
  }
  columnChooserView._popupContainer.option("position", {
    of: dataGrid.value.element(),
    my: "left top",
    at: "left top",
    offset: "0 50",
  });
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const clearFilter = () => {
  dataGrid.value.clearFilter("row");
  dataGrid.value.clearFilter("header");
  dataGrid.value.clearFilter("search");
  dataGrid.value.clearSorting();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/agents-instructions",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 10;
  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");

  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/agents-instructions",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/agents-instructions",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const refreshGrid = () => {
  document.querySelector(".modal-backdrop").remove();
  document.querySelector(".dx-device-desktop").classList.remove("modal-open");
  getAllAgentsInstructionsGrid();
};

const onItemClick = (type) => {
  if (type == "Excel") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "agents-instructions.xlsx"
        );
      });
    });
    // e.cancel = true;
  } else if (type == "CSV") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.csv.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "agents-instructions.csv"
        );
      });
    });
  } else if (type == "PDF") {
    const doc = new jsPDF("landscape");
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: dataGrid.value,
      customizeCell: function (options) {
        const { gridCell, pdfCell } = options;
        if (gridCell.rowType === "data") {
          // data columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            cellWidth: "auto",
          };
        } else {
          // header columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            fillColor: "#2680EB",
            textColor: "#FFFFFF",
            cellWidth: "auto",
          };
        }
      },
    }).then(() => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const header = "Users";
        const headerWidth = doc.getTextDimensions(header).w;
        const footer = `Page ${i} of ${pageCount}`;

        // Header
        doc.text(header, (pageWidth - headerWidth) / 2, 10);

        // Footer
        doc.text(
          footer,
          pageWidth / 2 - doc.getTextWidth(footer) / 2,
          pageHeight - 5,
          { baseline: "bottom" }
        );
      }

      doc.save("agents-instructions.pdf");
    });
  }
};

const redirectToCreate = () => {
  router.push("/agent-instructions");
};

const getAllAgentsInstructionsGrid = () => {
  agentInstructionsStore
    .fetchAllAgentsInstructionsGrid(companyId.value)
    .then((res) => {
      dataGrid.value.endCustomLoading();
      res.map((row) => {
        row.active_date = row.active_date
          ? moment(row.active_date).format("DD/MM/YYYY - hh:mm A")
          : "-";
        row.expiry_date = row.expiry_date
          ? moment(row.expiry_date).format("DD/MM/YYYY - hh:mm A")
          : "-";
        return row;
      });
    })
    .catch(() => {});
};

onMounted(() => {
  dataGrid.value.beginCustomLoading();
});

onBeforeMount(() => {
  getAllAgentsInstructionsGrid();
});
</script>

<template>
  <AgentLayout>
    <div class="flex items-center">
      <!-- <h4 class="grid-header-title">Users</h4>
        <div class="action-bar">
            <button :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!': ''" :disabled="checkLoggedInUserRole()" @click="userModal('show')" class="btn btn-primary">
              <i class="dx-icon-plus"></i> Add
            </button>
        </div> -->
      <!-- <button  pill class="grid-header-action-btn">
          Add
        </button> -->
    </div>
    <deleteAgentInstructions v-if="getModalState" @refresh="refreshGrid" />

    <PageHeader
      title="Agents Instructions"
      subtitle="You can create and manage prompts for agents. These instructions can be applied across stores, products, categories, and kiosks to control how the agent responds and interacts in different contexts."
      :showButton="true"
      buttonText="Add"
      @action-click="redirectToCreate()"
    />

    <section class="grid-white-section main-page-container-animation card">
      <DxDataGrid height="100%"
        :data-source="promptsGrid"
        ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="false"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="'No prompts yet'"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
      >
        <DxLoadPanel :show-indicator="true" :show-pane="true" :enabled="true" />
        <DxScrolling mode="standard" :use-native="true" />
        <DxPaging :page-size="10" />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />

        <!-- <DxFilterRow :visible="true" /> -->
        <DxSearchPanel :width="200" :visible="true" placeholder="Search ..." />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />
        <DxColumnChooser :enabled="true" mode="select" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
          <DxItem location="after" name="searchPanel" />
        </DxToolbar>

        <DxColumn
          data-field="title"
          caption="Title"
          :allow-header-filtering="false"
          :allow-searching="true"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="prompt"
          caption="Prompt"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="stores"
          caption="Stores"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="active_date"
          caption="Start Date"
          :allow-searching="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="expiry_date"
          caption="Expiry Date"
          :allow-searching="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="actions"
          caption="Actions"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :allow-sorting="false"
          cell-template="action-cell"
          data-type="object"
        />

        <template #action-cell="{ data }">
          <ActionCell :data="data.data" />
        </template>

        <!-- <template #columnButtonTemplate>
            <button class="custom-columns mx-1" @click="dataGrid.showColumnChooser()">
              <span class="custom-text">Columns </span>
              <span class="custom-icon">
                <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
              </span>
            </button>
          </template>

          <template #exportButtonTemplate>
            <DxDropDownButton class="exportGridHeader" :items="buttonData" text="Export" @item-click="onItemClick" />
          </template>

          <template #clearAllTemplate>
            <div v-if="showClearAll" class="clear-cols">
              <div class="custom-line"></div>
              <Button
                text="Clear All"
                @click="clearFilter()"
                styling-mode="outlined"
                type="danger"
                class="clear-colums-filter"
              />
            </div>
          </template> -->

        <template #exportButtonTemplate>
          <button
            @click="onItemClick('Excel')"
            type="button"
            class="btn icon-button m-5 font-20"
          >
            <i class="bi bi-filetype-xlsx"></i>
          </button>
        </template>

        <template #columnButtonTemplate>
          <button
            @click="dataGrid.showColumnChooser()"
            type="button"
            class="btn icon-button font-20 m-5"
          >
            <i class="bi bi-columns"></i>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols">
            <div class="custom-line"></div>
            <button
              @click="clearFilter()"
              type="button"
              class="btn action-btn clear-colums-filter m-5"
            >
              Clear Filters
            </button>
          </div>
        </template>
      </DxDataGrid>
    </section>
  </AgentLayout>
</template>
